import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Movie } from '../../types';
import { movieService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import MovieCard from '../MovieCard';
import './Hero.css';

const Hero: React.FC = () => {
  const { isAuthenticated, subscription, user } = useAuth();
  const navigate = useNavigate();
  const [featuredMovies, setFeaturedMovies] = useState<Movie[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFeaturedMovies = async () => {
      try {
        const movies = await movieService.getMovies(1, { limit: 12 });
        setFeaturedMovies(movies.slice(0, 12));
      } catch (error) {
        console.error('Failed to load featured movies:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFeaturedMovies();
  }, []);

  const getHeroButtonProps = () => {
    if (!isAuthenticated) {
      return {
        href: '/register',
        text: 'REGISTER NOW',
        onClick: undefined
      };
    }

    if (user?.role === 'admin' || subscription?.has_subscription) {
      return {
        href: '/watch-party/create',
        text: 'CREATE YOUR CLUB',
        onClick: undefined
      };
    }

    return {
      href: '#',
      text: 'START FREE TRIAL',
      onClick: (e: React.MouseEvent) => {
        e.preventDefault();
        navigate('/subscribe');
      }
    };
  };

  const buttonProps = getHeroButtonProps();

  return (
    <div className="relative min-h-[60vh] lg:min-h-[80vh] flex items-center py-12 lg:py-20" style={{ zIndex: 1 }}>
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Side - Text Content */}
          <div className="text-center lg:text-left">
            <h1 className="text-3xl sm:text-4xl lg:text-6xl xl:text-7xl font-black mb-4 lg:mb-6 leading-tight">
              Watch Movies <span className="pink-gradient-text">Together Online</span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl text-netflix-light-gray mb-6 lg:mb-8">
              Stream movies together with friends in real-time.
            </p>

            {/* Space Button */}
            <Link 
              to={buttonProps.href} 
              onClick={buttonProps.onClick}
              className="btn-space"
            >
              <strong>{buttonProps.text}</strong>
              <div id="container-stars">
                <div id="stars"></div>
              </div>
              <div id="glow">
                <div className="circle"></div>
                <div className="circle"></div>
              </div>
            </Link>
          </div>

          {/* Right Side - Featured Movies Grid */}
          <div className="relative mt-8 lg:mt-0">
            {/* Decorative Frame */}
            <div className="absolute -inset-6 bg-gradient-to-br from-netflix-red/20 to-red-600/20 rounded-3xl blur-2xl hidden lg:block"></div>

            {/* Movies Grid Container - Desktop */}
            <div className="relative bg-gradient-to-br from-netflix-red/5 to-red-600/5 p-4 rounded-3xl shadow-lg hidden lg:block">
              <div className="relative rounded-2xl" style={{ overflow: 'visible' }}>
                <div className="featured-movies grid grid-cols-6 grid-rows-2 gap-3" style={{ overflow: 'visible' }}>
                  {loading ? (
                    // Skeleton loaders
                    Array.from({ length: 12 }).map((_, i) => (
                      <div key={i} className="aspect-[2/3] bg-white/10 rounded-lg animate-pulse"></div>
                    ))
                  ) : (
                    featuredMovies.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} featured showLogo={true} />
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Floating Badge - Desktop */}
            <div className="absolute -bottom-4 left-2 lg:-bottom-6 lg:-left-4 text-white px-4 py-2 lg:px-8 lg:py-4 rounded-full shadow-xl font-bold text-sm lg:text-lg pointer-events-none z-40 hidden lg:block pink-gradient-bg">
              <i className="fas fa-fire animate-pulse"></i> Featured Movies
            </div>

            {/* Mobile: Horizontal Swipe Carousel */}
            <div className="lg:hidden">
              <div className="flex items-center justify-between mb-3">
                <span className="text-white font-bold text-lg flex items-center gap-2">
                  <i className="fas fa-fire text-netflix-red animate-pulse"></i> Featured Movies
                </span>
                <span className="text-white/40 text-xs">
                  <i className="fas fa-hand-pointer"></i> Swipe
                </span>
              </div>
              <div className="featured-swipe-container flex gap-3 overflow-x-auto snap-x snap-mandatory pb-4">
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="flex-shrink-0 w-[120px] snap-start">
                      <div className="aspect-[2/3] bg-white/10 rounded-lg animate-pulse"></div>
                    </div>
                  ))
                ) : (
                  featuredMovies.map((movie) => (
                    <Link
                      key={movie.id}
                      to={`/movies/${movie.id}`}
                      className="featured-swipe-card flex-shrink-0 w-[120px] snap-start"
                    >
                      <div className="relative aspect-[2/3] overflow-hidden rounded-lg">
                        <img
                          src={movie.thumbnail.startsWith('http') ? movie.thumbnail : `/public/uploads/thumbnails/${movie.thumbnail}`}
                          alt={movie.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                        <div className="absolute bottom-1 left-1 right-1">
                          <p className="text-white text-[10px] font-bold truncate">{movie.title}</p>
                          <span className="inline-flex items-center bg-yellow-400 text-black px-1 py-0.5 rounded text-[8px] font-bold">
                            IMDb {movie.rating || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Transition gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent via-transparent to-black/50 pointer-events-none z-5"></div>
    </div>
  );
};

export default Hero;
